<!--
  Used to display the Foxit PDF viewer.
  Note: Offline only works in production, because pre-caching is disabled in development.
-->
<template>
	<div
		class="relative h-full w-full transition-all duration-200"
		:class="{
			'translate-y-5 opacity-0':
				sessionDocumentsStore.pageState.status !== 'ready',
			'translate-y-0 opacity-100':
				sessionDocumentsStore.pageState.status === 'ready',
		}"
		id="foxit-container"
	>
		<div
			v-if="showToolbar"
			class="absolute top-2 right-48 z-90 flex gap-2"
		>
			<button
				class="flex items-center justify-center border-0 border-transparent! bg-gray-200! px-2 py-1 text-sm text-gray-900! hover:bg-gray-300!"
				name="autofill-btn"
				id="autofill-btn"
				tabindex="0"
				aria-label="autofill"
				@click="resetButton"
			>
				RESET FORM
			</button>
			<button
				class="flex items-center justify-center border-0 border-transparent! bg-gray-200! px-2 py-1 text-sm text-gray-900! hover:bg-gray-300!"
				name="change-payment-type-btn"
				id="change-payment-type-btn"
				tabindex="0"
				aria-label="change payment type"
				@click="changePaymentDetailsButton"
			>
				CHANGE PAYMENT DETAILS
			</button>
		</div>

		<div
			id="pdf-ui"
			class="isolate h-full w-full bg-[#fbfbfd] pb-2 transition-opacity duration-300"
			:class="{
				'opacity-0': sessionDocumentsStore.pageState.status !== 'ready',
			}"
		></div>

		<!-- Overlay to prevent interaction when document is completed -->
		<div
			v-if="sessionDocumentsStore.currentStep?.completed"
			class="absolute inset-4 z-50 mt-16 mr-4 bg-slate-400/20"
			:title="t('document.documentCompletedRequest')"
		></div>

		<ResetFormModal @confirmResetForm="confirmResetForm" />

		<Teleport
			defer
			to="#main-content"
			v-if="changePaymentDetailsModalOpen"
		>
			<ChangePaymentDetailsModal
				@confirmPayment="confirmPayment"
				@closeModal="changePaymentDetailsModalOpen = false"
			/>
		</Teleport>

		<ValidateFormModal
			v-model:open="validateModalOpen"
			:unfilledRequiredFields="unfilledRequiredFields"
			:validatedSignatures="validatedSignatures"
			:validatedFuneralDirectorSignatures="validatedFuneralDirectorSignatures"
			@finalize="finalizeDocument"
		/>

		<CustomItemModal
			:open="customItemModalOpen"
			:plan="currentPlan"
			:package-included="customItemModalPackageIncluded"
			@close="customItemModalOpen = false"
			@submit="handleAddCustomItem"
		/>
		<PrecoaModal
			v-model:open="isShowingConfirmation"
			@close="cancelCustomItemConfirmation"
		>
			<div
				class="flex flex-col items-center justify-center gap-2 px-4 pt-6 text-center"
				aria-label="Confirmation Warning View"
			>
				<ExclamationTriangleIcon class="h-10 w-10 text-red-500!" />
				<h1 class="text-lg font-bold">
					{{ t('document.changePaymentDetailsModal.confirmationTitle') }}
				</h1>
				<p class="text-center text-sm text-slate-500!">
					{{
						t('document.changePaymentDetailsModal.customItemMessage', {
							forms: formsToInvalidate.join(', '),
						})
					}}
				</p>
			</div>
			<template #buttons>
				<div class="flex w-full flex-col gap-2">
					<button
						type="button"
						aria-label="Confirm Custom Item Confirmation"
						class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! mb-2 w-full rounded-md py-2 font-bold first-letter:rounded-lg disabled:bg-slate-300! disabled:text-slate-500/50!"
						@click="handleConfirmation"
					>
						{{ t('document.confirm') }}
					</button>
					<button
						type="button"
						aria-label="Cancel Custom Item Confirmation"
						class="pb-2 font-bold text-red-500! underline!"
						@click="cancelCustomItemConfirmation"
					>
						{{ t('general.cancel') }}
					</button>
				</div>
			</template>
		</PrecoaModal>
	</div>
</template>

<script setup lang="ts">
import { useFoxitManager } from './composables/FoxitManager'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import CustomItemModal from '@/components/documents/CustomItems/CustomItemModal.vue'
import ChangePaymentDetailsModal from '@/components/documents/Foxit/ChangePaymentDetails/ChangePaymentDetailsModal.vue'
import ResetFormModal from '@/components/documents/Foxit/ResetFormModal.vue'
import ValidateFormModal from '@/components/documents/Foxit/ValidateFormModal.vue'
import { useFoxitFormFilling } from '@/components/documents/Foxit/composables/FoxitFormFilling'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { useFoxitUI } from '@/components/documents/Foxit/composables/FoxitUI'
import { DocumentType } from '@/enums/DocumentType'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import type { Step } from '@/stores/sessionDocuments'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { CustomCartItem } from '@/types'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const { t } = usePresentationLocale()
const sessionDocumentsStore = useSessionDocumentsStore()
const planStore = usePlanStore()
const {
	updateAutofillButton,
	updateChangePaymentTypeButton,
	updateAddCustomItemButton,
} = useFoxitUI()
const {
	validatedSignatures,
	validatedFuneralDirectorSignatures,
	unfilledRequiredFields,
	finalizeDocument,
	validateModalOpen,
	setFieldValues,
	setCustomItemFieldValues,
} = useFoxitFormFilling()
const {
	pageStateListener,
	registerEventEmitters,
	confirmResetForm,
	confirmPayment,
} = useFoxitManager()

const { prepareUiandViewer, showToolbar } = useFoxitInstance()
const changePaymentDetailsModalOpen = ref(false)
const customItemModalOpen = ref(false)
const isShowingConfirmation = ref(false)
const customItemToAdd = ref<Omit<
	CustomCartItem,
	'id' | 'isSoftDeleted'
> | null>(null)
let stopPersistingSignatures: (() => void) | undefined
let unmounted = false

const openModal = () => {
	changePaymentDetailsModalOpen.value = true
}

const openCustomItemModal = () => {
	customItemModalOpen.value = true
}

const currentPlan = computed(() => {
	return planStore.getPlan(sessionDocumentsStore.currentPlanHolderID)
})

const customItemModalPackageIncluded = computed(() => {
	if (!currentPlan.value) {
		return false
	}

	return planStore.planIncludesPackage(currentPlan.value?.id)
})

const paymentAuthorizationStep = computed(
	(): Step | undefined =>
		sessionDocumentsStore.getStepByLabel(DocumentType.PaymentAuthorization) ??
		undefined
)

const enrollmentStep = computed(
	(): Step | undefined =>
		sessionDocumentsStore.getStepByLabel(DocumentType.EnrollmentForm) ??
		undefined
)

const formsToInvalidate = computed<string[]>(() => {
	const result = []

	if (enrollmentStep.value?.completed) {
		result.push(enrollmentStep.value.label)
	}

	if (paymentAuthorizationStep.value?.completed) {
		result.push(paymentAuthorizationStep.value.label)
	}

	return result
})

const invalidateForms = async () => {
	const stepsToMarkAsIncomplete = [
		paymentAuthorizationStep.value,
		enrollmentStep.value,
	]

	for (const step of stepsToMarkAsIncomplete) {
		if (!step) {
			continue
		}

		await sessionDocumentsStore.setStepCompleted(step, false)
		sessionDocumentsStore.setStepAutoFilled(step, false)
	}
}

/**
 * A custom item changes the calculated values in both payment forms. They can
 * no longer be considered auto-filled, even when the forms have not yet been
 * completed and therefore do not require an invalidation confirmation.
 */
const resetFormsAutofill = () => {
	for (const step of [paymentAuthorizationStep.value, enrollmentStep.value]) {
		if (!step) {
			continue
		}

		sessionDocumentsStore.setStepAutoFilled(step, false)
	}
}

/**
 * Adds a custom item to the current plan and resets the forms autofill.
 */
const addCustomItem = (item: Omit<CustomCartItem, 'id' | 'isSoftDeleted'>) => {
	const customItemId = currentPlan.value?.addCustomItem(item)
	resetFormsAutofill()
	customItemModalOpen.value = false

	if (customItemId !== undefined) {
		setCustomItemFieldValues(customItemId)
	}
}

/**
 * Handles the addition of a custom item.
 * If the forms to invalidate are not empty, shows a confirmation modal.
 * Otherwise, adds the custom item and resets the forms autofill.
 */
const handleAddCustomItem = (item: any) => {
	if (formsToInvalidate.value.length > 0) {
		isShowingConfirmation.value = true
		customItemToAdd.value = item
		return
	}

	addCustomItem(item)
}

/**
 * Handles the confirmation of the addition of a custom item.
 * Invalidates the forms and adds the custom item.
 */
const handleConfirmation = async () => {
	if (!customItemToAdd.value) {
		return
	}

	addCustomItem(customItemToAdd.value)
	isShowingConfirmation.value = false
	customItemToAdd.value = null

	await invalidateForms()
}

const cancelCustomItemConfirmation = () => {
	isShowingConfirmation.value = false
	customItemToAdd.value = null
}

onMounted(async () => {
	document.addEventListener('foxit-change-payment-type-button', openModal)
	document.addEventListener('foxit-add-custom-item-button', openCustomItemModal)
	await sessionDocumentsStore.setPageState('pending')
	await prepareUiandViewer()
	if (!unmounted) {
		stopPersistingSignatures = registerEventEmitters()
	}
})

onUnmounted(() => {
	unmounted = true
	stopPersistingSignatures?.()
	document.removeEventListener('foxit-change-payment-type-button', openModal)
	document.removeEventListener(
		'foxit-add-custom-item-button',
		openCustomItemModal
	)
})

watch(() => sessionDocumentsStore.pageState, pageStateListener, {
	immediate: true,
})

watch(
	() => sessionDocumentsStore.currentStep,
	async (step) => {
		await new Promise((resolve) => setTimeout(resolve, 100))
		updateAutofillButton()
		updateChangePaymentTypeButton()
		updateAddCustomItemButton()
	},
	{ immediate: true, deep: true }
)

defineExpose({
	setFieldValues,
})

const resetButton = () => {
	document.dispatchEvent(new CustomEvent('foxit-autofill-button'))
}

const changePaymentDetailsButton = () => {
	document.dispatchEvent(new CustomEvent('foxit-change-payment-type-button'))
}
</script>

<style>
@reference "tailwindcss";
/** adjust the toolbar tabs spacing */
[component-name='toolbar-tabs'] {
	@apply m-0 py-1;
}

/** make the previous and next page buttons smaller */
[component-name='goto-prev-page'],
[component-name='goto-next-page'] {
	width: 20px !important;
}

/** enlarge the width of the signature dialog */
div[component-name='create-signature'] .fv__ui-layer-inner {
	width: 80vw;
	max-width: 1000px;
}

/** make the typed signature preview tall enough for cursive swashes (Foxit's default is 120px text in a 154px box) */
div.fv__ui-sign-dialog-typePreview {
	@apply h-[220px]! text-[96px]! leading-[220px]!;
}

/** set the background color of required fields to yellow */
div.fv__ui-form-foreground.required {
	background-color: rgba(246, 239, 194, 0.8) !important;
}

/** reverse the order of the tabs in the signature dialog */
div.fv__ui-sign-dialog-tabs {
	@apply flex-row-reverse;
}

/** hide the save signature checkbox in the signature dialog (we always want it on) */
label.fv__ui-sign-dialog-save {
	@apply hidden!;
}

/** customize eraser button in the signature dialog */
div.fv__ui-sign-dialog-eraser {
	@apply h-[32px] rounded-lg bg-[#a7a7a7] pr-12 text-left;
}

div.fv__ui-sign-dialog-eraser:hover {
	@apply bg-[#fa5050];
}

i[name='sign-dialog-select-eraser'] {
	@apply relative ml-1!;
}

i[name='sign-dialog-select-eraser']::after {
	@apply absolute left-[85%] h-full pt-0.5 pl-2 text-white not-italic content-['Clear'];
}
</style>
